import { z } from 'zod'
import { transcriptMeta } from './transcript'

export const tokenCapability = z.enum(['read', 'edit', 'download'])
export type TokenCapability = z.infer<typeof tokenCapability>
export const createTokenBody = z.object({
  label: z.string().trim().min(1).max(40).regex(/^[a-z0-9 ._-]+$/),
  days: z.number().int().min(1).max(90).default(30),
  capabilities: z.array(tokenCapability).min(1).max(3).refine((v) => new Set(v).size === v.length && v.includes('read'), 'Include read, without duplicate capabilities').default(['read']),
}).strict()
export const apiToken = z.object({
  id: z.string(), label: z.string(), prefix: z.string(), capabilities: z.array(tokenCapability),
  expiresAt: z.string(), lastUsedAt: z.string().nullable(), lastUsedIp: z.string().nullable(),
  revokedAt: z.string().nullable(), createdAt: z.string(),
})
export type ApiToken = z.infer<typeof apiToken>
export const createdToken = z.object({ token: z.string(), info: apiToken })
export type CreatedToken = z.infer<typeof createdToken>

import { ASPECTS, RESOLUTIONS, recordingSummary, render, source, SOURCE_STATUSES, meResponse, createImportBody, addImportFilesBody, importUrlBody, importDetail } from './api'
import { LAYOUT_IDS } from './layouts'
export { editOp, editBatchBody } from './edit-ops'
export const v1Id = z.string().uuid()
export const v1IdParams = z.object({ id: v1Id }).strict()
export const v1Empty = z.object({}).strict()
export const boundaryDto = z.object({ id: z.string(), t: z.number(), layout: z.enum(LAYOUT_IDS), slots: z.array(z.string()), cut: z.boolean(), locked: z.boolean() })
export const markerDto = z.object({ id: z.string(), t: z.number(), label: z.string(), color: z.string(), author: z.string() })
export const audioDto = z.record(z.string(), z.object({ gainDb: z.number(), muted: z.boolean() }))
export const takeSpanDto = z.object({ takeId: z.string(), index: z.number(), start: z.number(), duration: z.number() })
export const segmentDto = z.object({ boundaryId: z.string(), start: z.number(), end: z.number(), layout: z.enum(LAYOUT_IDS), slots: z.array(z.string()), cut: z.boolean(), takeId: z.string(), takeOffset: z.number() })
export const agentSource = source.extend({
  person: z.string(),
  // Old idempotent batch results predate transcripts; serialize their metadata as null.
  transcript: transcriptMeta.nullable().default(null),
  availability: z.array(z.object({ takeId: z.string(), ranges: z.array(z.tuple([z.number(), z.number()])), status: z.enum(SOURCE_STATUSES) })),
  status: z.enum(SOURCE_STATUSES),
})
export const agentSnapshot = z.object({
  recording: recordingSummary, projectId: z.string(), aspect: z.enum(ASPECTS), takes: z.array(takeSpanDto), sources: z.array(agentSource),
  boundaries: z.array(boundaryDto), markers: z.array(markerDto), audio: audioDto, segments: z.array(segmentDto),
  timelineMs: z.number(), outputMs: z.number(), mediaReady: z.boolean(),
  layouts: z.record(z.string(), z.object({ label: z.string(), hint: z.string(), maxSlots: z.number() })),
})
export type AgentSnapshot = z.infer<typeof agentSnapshot>
export const batchResult = z.object({ batchId: z.string().nullable(), diffCount: z.number(), snapshot: agentSnapshot })
export type BatchResult = z.infer<typeof batchResult>
export const editBatchDto = z.object({
  id: z.string(), projectId: z.string(), tokenId: z.string().nullable(), userId: z.string().nullable(), label: z.string(), note: z.string().nullable(),
  origin: z.string().nullable(), sourceRecordingId: z.string().nullable(),
  ops: z.array(z.json()), diffCount: z.number(), status: z.enum(['applied', 'reverted']), createdAt: z.string(), revertedAt: z.string().nullable(),
})
export type EditBatchDto = z.infer<typeof editBatchDto>
export const agentExport = render.extend({ webUrl: z.string() })
export type AgentExport = z.infer<typeof agentExport>
export const agentMe = meResponse.extend({ token: z.object({ label: z.string(), capabilities: z.array(tokenCapability) }),
  teams: z.array(meResponse.shape.teams.element.extend({ studios: z.array(z.object({ id: z.string(), name: z.string() })) })),
})
export type AgentMe = z.infer<typeof agentMe>
export const exportBody = z.object({ resolution: z.enum(RESOLUTIONS).default('720p') }).strict()
export const idempotencyHeaders = z.object({ 'idempotency-key': z.string().min(1).max(200).regex(/^[\x21-\x7E]+$/) }).passthrough()
export const recordingQuery = z.object({ teamId: v1Id.optional() }).strict()
export { createImportBody as v1CreateImportBody, importUrlBody as v1ImportUrlBody, importDetail as v1ImportDetail }

export const v1AddImportFilesBody = addImportFilesBody
export const importUploadPart = z.object({ n: z.number().int().min(1).max(10000), etag: z.string().min(1).max(200) })
export const importUploadPlan = z.object({ partSize: z.number().int().positive().max(64 * 1024 ** 2), partCount: z.number().int().min(1).max(10000), done: z.array(importUploadPart).max(10000), uploaded: z.boolean() })
export const importCompleteBody = z.object({ parts: z.array(importUploadPart).min(1).max(10000) }).strict()
export type ImportUploadPlan = z.infer<typeof importUploadPlan>
export type ImportUploadPart = z.infer<typeof importUploadPart>

export const v1RecordingSummary = recordingSummary.extend({ importId: z.string().nullable() })
export type AgentRecordingSummary = z.infer<typeof v1RecordingSummary>

/** Metadata only: no object keys, signed URLs or media downloads. */
export const renderInputsQuery = exportBody.extend({ aspect: z.enum(ASPECTS).optional() }).strict()
export const renderPlanDto = z.object({
  resolution: z.enum(RESOLUTIONS), aspect: z.enum(ASPECTS), canvas: z.object({ w: z.number().int().positive(), h: z.number().int().positive() }),
  audio: audioDto, segments: z.array(z.object({ takeId: z.string(), from: z.number().nonnegative(), duration: z.number().positive(), layout: z.enum(LAYOUT_IDS), slots: z.array(z.string()) })),
})
export const renderOriginal = z.object({
  trackId: z.string(), name: z.string(), bytes: z.number().int().positive(), sha256: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
  clockFactor: z.number().positive(), audioRate: z.number().int().positive().nullable(), hasAudio: z.boolean(),
})
export const renderSource = z.object({
  takeId: z.string(), sourceId: z.string(), participantId: z.string(), sourceKey: z.string(), kind: z.enum(['camera', 'screen', 'mic']),
  takeDurationMs: z.number().positive(), width: z.number().int().positive().nullable(), height: z.number().int().positive().nullable(), hasAudio: z.boolean(),
  originals: z.array(renderOriginal), placements: z.array(z.object({ trackId: z.string(), at: z.number().nonnegative(), skip: z.number().nonnegative(), duration: z.number().positive() })),
})
export const renderInputs = z.object({ recordingId: z.string(), title: z.string(), plan: renderPlanDto, sources: z.array(renderSource) })
export type RenderInputs = z.infer<typeof renderInputs>
export type RenderSource = z.infer<typeof renderSource>
